import React from 'react';

import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { PencilIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Pencil';
import H from '@zola/zola-ui/src/typography/Headings';

import EditLocationModal from '~/pages/couples/explore/components/SearchBar/EditLocationModal';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { onEnterKeyDown } from '~/util/accessibilityUtils';

import { useSearchResultsSeoContext } from '../../contexts/SearchResultsSeoContext';

import styles from './sharedPageTitle.module.less';

type EditLocationButtonProps = {
  ariaHidden?: boolean;
  editLocationAriaLabel?: string;
  onLocationClick: () => void;
};

const EditLocationButton = ({
  ariaHidden = false,
  editLocationAriaLabel = '',
  onLocationClick,
}: EditLocationButtonProps) => (
  <LinkV2
    className={styles.editLocation}
    aria-hidden={ariaHidden}
    aria-label={editLocationAriaLabel}
    role="button"
    onClick={onLocationClick}
    tabIndex={ariaHidden ? -1 : 0}
  >
    <PencilIcon height={20} width={20} title="" />
  </LinkV2>
);

type SharedPageTitleProps = {
  onCategoryClick: () => void;
  onLocationClick: () => void;
  onUpdateSelectedLocation: (location: LocationOrAllMarketsType) => void;
  setShowLocationModal: (show: boolean) => void;
  showLocationModal: boolean;
};

/**
 * SRP page title shared between pre-auth and post-auth implementations
 * - Pre-auth (~/pages/couples/explore/components/SearchResults/components/PreAuth/PageHeading)
 * - Post-auth (~/pages/couples/explore/components/SearchResults/components/PageTitle)
 */
export const SharedPageTitle = ({
  onCategoryClick,
  onLocationClick,
  setShowLocationModal,
  showLocationModal,
  onUpdateSelectedLocation,
}: SharedPageTitleProps) => {
  const { heading, headingPrefix, headingCategory, headingLocation } = useSearchResultsSeoContext();
  const editLocationAriaLabel = `Edit search location ${headingLocation}`;

  return (
    <>
      <div className={styles.desktopPageTitle}>
        <H.TitleMedium1 presentation="h4">{heading}</H.TitleMedium1>
      </div>
      <div className={styles.mobilePageTitle}>
        <H.TitleMedium1 presentation="h4">
          {headingPrefix && `${headingPrefix} `}
          <span
            className={styles.clickable}
            aria-label={`Edit search category ${headingCategory}`}
            onClick={onCategoryClick}
            onKeyDown={onEnterKeyDown(onCategoryClick)}
            role="button"
            tabIndex={0}
          >
            {headingCategory}
          </span>
          {headingLocation ? (
            <>
              {' '}
              {headingLocation !== 'Near You' && 'in '}
              <span
                className={styles.clickable}
                aria-label={editLocationAriaLabel}
                onClick={onLocationClick}
                onKeyDown={onEnterKeyDown(onCategoryClick)}
                role="button"
                tabIndex={0}
              >
                <span className={styles.noWrap}>
                  {headingLocation}
                  <EditLocationButton ariaHidden onLocationClick={onLocationClick} />
                </span>
              </span>
            </>
          ) : (
            <EditLocationButton
              editLocationAriaLabel={editLocationAriaLabel}
              onLocationClick={onLocationClick}
            />
          )}
        </H.TitleMedium1>
        {showLocationModal && (
          <EditLocationModal
            hideModalFn={() => setShowLocationModal(false)}
            onSelect={onUpdateSelectedLocation}
          />
        )}
      </div>
    </>
  );
};
